import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";
import { positions, positionColors, type Position } from "@/mocks/positionsData";
import QuickCreateDropdown from "@/components/feature/QuickCreateDropdown";

// ── Nav items grouped by section ──
type NavItem = {
  icon: string;
  label: string;
  path: string;
  end?: boolean;
  indicator?: string;
  adminOnly?: boolean;
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    id: "main",
    label: "MAIN",
    items: [
      { icon: "ri-dashboard-line", label: "Dashboard", path: "/dashboard", end: true },
      { icon: "ri-pulse-line", label: "Activity Feed", path: "/activity" },
    ],
  },
  {
    id: "sales",
    label: "SALES",
    items: [
      { icon: "ri-user-star-line", label: "Leads", path: "/leads" },
      { icon: "ri-flow-chart", label: "Pipeline", path: "/pipeline" },
      { icon: "ri-whatsapp-line", label: "WhatsApp", path: "/whatsapp", indicator: "green" },
      { icon: "ri-phone-line", label: "Calls", path: "/calls" },
      { icon: "ri-file-text-line", label: "Quotations", path: "/quotations" },
      { icon: "ri-building-line", label: "Projects", path: "/projects" },
    ],
  },
  {
    id: "marketing",
    label: "MARKETING",
    items: [
      { icon: "ri-megaphone-line", label: "Campaigns", path: "/campaigns" },
      { icon: "ri-global-line", label: "Web Forms", path: "/lead-capture" },
    ],
  },
  {
    id: "analytics",
    label: "ANALYTICS",
    items: [
      { icon: "ri-bar-chart-2-line", label: "Reports", path: "/reports" },
      { icon: "ri-plug-line", label: "Integrations", path: "/integrations", adminOnly: true },
    ],
  },
  {
    id: "settings",
    label: "SETTINGS",
    items: [
      { icon: "ri-settings-3-line", label: "Settings", path: "/settings" },
    ],
  },
];

const navLinkBaseCollapsed =
  "flex items-center justify-center w-11 h-11 mx-auto rounded-lg transition-all duration-200";
const navLinkBase =
  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap";
const navLinkInactive = `${navLinkBase} text-[#8b92a9] hover:text-white hover:bg-[#252b3d]`;
const navLinkActive = `${navLinkBase} bg-indigo-600 text-white`;

type Props = {
  mobileMenuOpen: boolean;
  onCloseMobile: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
};

export function CrmSidebar({ mobileMenuOpen, onCloseMobile, collapsed, onToggleCollapse }: Props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  const canAccessSettings = user && (user.role.toLowerCase() === "super_admin" || user.role.toLowerCase() === "admin");

  // Find user's position
  const userPosition: Position | undefined = (() => {
    if (!user) return undefined;
    const roleToPositionId: Record<string, string> = {
      super_admin: "pos-1",
      admin: "pos-2",
      sales_manager: "pos-3",
      sales_executive: "pos-6",
      viewer: "pos-8",
    };
    const posId = roleToPositionId[user.role] || "pos-6";
    return positions.find((p) => p.id === posId);
  })();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const posColor = userPosition?.color || "#4f46e5";
  const posColorClass = positionColors[posColor] || "bg-indigo-100 text-indigo-700";

  return (
    <aside
      className={`fixed lg:static inset-y-0 left-0 z-40 bg-sidebar-bg flex flex-col transition-all duration-300 ${collapsed ? "w-14" : "w-64"
        } ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
    >
      {/* ── Sidebar header ── */}
      {collapsed ? (
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center h-14 border-b border-sidebar-border text-white hover:bg-[#252b3d] transition-colors cursor-pointer"
          title="Expand sidebar"
        >
          <i className="ri-menu-line text-lg w-5 h-5 flex items-center justify-center" />
        </button>
      ) : (
        <div className="flex items-center gap-3 px-5 py-5 border-b border-sidebar-border">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-[#252b3d] text-white transition-colors cursor-pointer flex-shrink-0"
            title="Collapse sidebar"
          >
            <i className="ri-menu-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
          <NavLink
            to="/dashboard"
            end
            onClick={onCloseMobile}
            className="flex items-center gap-3 flex-1 min-w-0"
          >
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-pie-chart-2-fill text-lg text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight text-white truncate">CRM Pro</span>
          </NavLink>
        </div>
      )}

      {/* ── Nav items ── */}
      <nav className="flex-1 px-1.5 py-3 overflow-y-auto">
        {/* Quick Create button */}
        {!collapsed && (
          <div className="mb-4 px-1.5">
            <QuickCreateDropdown />
          </div>
        )}

        {navSections.map((section) => {
          const visibleItems = section.items.filter(
            (item) => !item.adminOnly || canAccessSettings
          );
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.id} className={section.id !== "main" ? "mt-1" : ""}>
              {/* Section label */}
              {!collapsed && (
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#6b7280] px-3 py-2 mt-3 mb-0.5">
                  {section.label}
                </p>
              )}

              {visibleItems.map((item) => (
                <NavLink
                  key={item.label + item.path}
                  to={item.path}
                  end={item.end}
                  onClick={onCloseMobile}
                  className={({ isActive }) =>
                    collapsed
                      ? `${navLinkBaseCollapsed} ${isActive
                        ? "bg-indigo-600 text-white"
                        : "text-[#8b92a9] hover:text-white hover:bg-[#252b3d]"
                      }`
                      : isActive
                        ? navLinkActive
                        : navLinkInactive
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <div className="relative">
                    <i
                      className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}
                    />
                    {item.indicator === "green" && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 border border-[#1a1f2e]" />
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      {item.label}
                      {item.indicator === "green" && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500" />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* ── Sidebar footer / user card ── */}
      {!collapsed && (
        <div className="px-5 py-4 border-t border-sidebar-border">
          <div className="relative">
            <div className="flex items-center gap-3 mb-4 bg-sidebar-card rounded-lg p-3">
              <button
                onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
                className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all"
              >
                {initials}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
                <p className="text-xs text-gray-400 truncate">{user.email}</p>
                <span
                  className={`inline-block mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${posColorClass}`}
                >
                  {userPosition?.name || "Sales Agent"}
                </span>
              </div>
            </div>

            {/* Avatar dropdown */}
            {avatarMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setAvatarMenuOpen(false)}
                />
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-1 overflow-hidden">
                  <button
                    onClick={() => {
                      navigate("/settings/profile");
                      setAvatarMenuOpen(false);
                      onCloseMobile();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-user-line text-gray-400 w-4 h-4 flex items-center justify-center" />
                    My Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate("/settings");
                      setAvatarMenuOpen(false);
                      onCloseMobile();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-settings-3-line text-gray-400 w-4 h-4 flex items-center justify-center" />
                    Settings
                  </button>
                  <hr className="my-1 border-gray-100" />
                  <button
                    onClick={() => {
                      handleLogout();
                      setAvatarMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line w-4 h-4 flex items-center justify-center" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-[#8b92a9] hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-lg w-5 h-5 flex items-center justify-center" />
            Logout
          </button>
        </div>
      )}

      {/* Collapsed: avatar only */}
      {collapsed && (
        <div className="px-1.5 py-3 border-t border-sidebar-border">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-11 h-11 mx-auto rounded-lg text-[#8b92a9] hover:text-red-400 hover:bg-red-400/10 transition-all duration-200"
            title="Logout"
          >
            <i className="ri-logout-box-r-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
      )}
    </aside>
  );
}